import React, { useState, useCallback } from 'react';
import { Mail, Upload, Send, Settings, AlertCircle, Globe, Sparkles, Search, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase, getCurrentUser } from '../lib/supabase';
import { sendTestEmail, checkPreviouslySentEmail, recordSentEmail, getAllSentEmails } from '../lib/emailService';
import { enhanceProductDescription, searchBusinessEmails } from '../lib/aiApi';
import { EmailCampaignSetup, GatheredEmailsResponse, EmailWithSelection, CampaignTimerConfig, EmailSearchSettings } from '../types';
import { emailConfig } from '../config/email';
import FileUpload from './FileUpload';
import CampaignSummary from './CampaignSummary';
import ConfirmationModal from './ConfirmationModal';
import EmailSelection from './EmailSelection';
import EmailPreview from './EmailPreview';
import EmailTemplateGenerator from './EmailTemplateGenerator';
import EmailSearchSettingsModal from './EmailSearchSettingsModal';
import SpreadsheetImport from './SpreadsheetImport';
import { generateAISubject, assignCountryFromEmail, getPoliteGreeting } from '../utils/emailUtils';
import EmailCampaignResults from './EmailCampaignResults';

export default function EmailCampaignForm() {
  const [formData, setFormData] = useState<EmailCampaignSetup>({
    businessCategory: '',
    productDescription: '',
    productImage: null,
    animatedGif: null,
  });
  const [loading, setLoading] = useState(false);
  const [emailsData, setEmailsData] = useState<GatheredEmailsResponse | null>(null);
  const [emailsWithSelection, setEmailsWithSelection] = useState<EmailWithSelection[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadedUrls, setUploadedUrls] = useState({
    productImage: '',
    animatedGif: '',
  });
  const [showSearchSettings, setShowSearchSettings] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [campaignInProgress, setCampaignInProgress] = useState(false);
  const [timerConfig, setTimerConfig] = useState<CampaignTimerConfig>({
    interval: 10,
    startTime: null,
    totalSent: 0,
    totalToSend: 0,
    nextSendTime: null,
    emailTemplate: `${emailConfig.senderName} <${emailConfig.senderEmail}>\nSubject: New Campaign Update\n\nHi {{recipient}},\n\nWe're excited to share our latest updates with you. Check out our new products and exclusive offers!\n\nRegards,\n${emailConfig.senderName}\nVantaPrints`,
    isPaused: false
  });
  const [searchSettings, setSearchSettings] = useState<EmailSearchSettings>({
    minEmails: 3,
    maxEmails: 200,
    includeBusinessEmails: true,
    includePersonalEmails: true,
    prioritizeDomains: ['mt', 'com.mt', 'org.mt', 'malta', 'gozo', 'valletta'],
    excludeDomains: [],
    targetCountries: ['MT'], // Focus only on Malta
    maxEmailsPerDomain: 2,
    apiKey: localStorage.getItem('openai_api_key') || ''
  });
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [previouslySentEmails, setPreviouslySentEmails] = useState<{ email: string, sentDate: string }[]>([]);
  const [importedEmails, setImportedEmails] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [testEmailSent, setTestEmailSent] = useState(false);

  // Load previously sent emails when component mounts
  React.useEffect(() => {
    const loadSentEmails = async () => {
      try {
        const sentEmails = await getAllSentEmails();
        setPreviouslySentEmails(sentEmails);
      } catch (error) {
        console.error("Error loading sent emails:", error);
      }
    };
    
    loadSentEmails();
  }, []);

  // Update emails with selection when emailsData changes
  React.useEffect(() => {
    if (emailsData?.gatheredEmails) {
      const processEmails = async () => {
        try {
          const emailsWithStatus = await Promise.all(
            emailsData.gatheredEmails.map(async (email) => {
              // Check if email was previously sent
              const { sent, date } = await checkPreviouslySentEmail(email);
              
              return {
                email,
                selected: false,
                country: assignCountryFromEmail(email, searchSettings.targetCountries),
                previouslySent: sent,
                sentDate: date,
                sourceUrl: emailsData.sourceUrls?.[email]?.url,
                sourceName: emailsData.sourceUrls?.[email]?.name
              };
            })
          );
          
          setEmailsWithSelection(emailsWithStatus);
        } catch (error) {
          console.error("Error processing emails:", error);
          toast.error("Failed to process email data");
        }
      };
      
      processEmails();
    }
  }, [emailsData, searchSettings.targetCountries]);

  // When emailsData changes and contains an enhanced description, update the email template
  React.useEffect(() => {
    if (emailsData?.enhancedDescription) {
      try {
        // Generate AI subject line based on business category and product description
        const aiSubject = generateAISubject(formData.businessCategory, formData.productDescription);
        
        // Update the email template with the enhanced description and new subject
        setTimerConfig(prev => {
          // Split the current template to get sender
          const lines = prev.emailTemplate.split('\n');
          const sender = lines[0]; // Luca <info@vantaprints.com>
          
          // Create new template with enhanced description and polite greeting
          const newTemplate = `${sender}\nSubject: ${aiSubject}\n\nHi there,\n\n${emailsData.enhancedDescription}\n\nRegards,\n${emailConfig.senderName}\nVantaPrints`;
          
          return {
            ...prev,
            emailTemplate: newTemplate
          };
        });
        
        // Send a test email with the new template if not already sent
        if (!testEmailSent) {
          sendTestEmailToSender(aiSubject, emailsData.enhancedDescription);
          setTestEmailSent(true);
        }
      } catch (error) {
        console.error("Error updating email template:", error);
        toast.error("Failed to update email template");
      }
    }
  }, [emailsData, formData.businessCategory, formData.productDescription, testEmailSent]);

  // Send a test email to the sender's address
  const sendTestEmailToSender = async (subject: string, content: string) => {
    try {
      const user = await getCurrentUser();
      
      if (!user) {
        toast.error('Please sign in to send emails');
        return;
      }
      
      // Format the email content with HTML for better readability
      const emailContent = `
        <p>Hi ${emailConfig.senderName},</p>
        
        <p>This is a test email for your upcoming Malta campaign.</p>
        
        <p>${content}</p>
        
        <p>Regards,<br>
        ${emailConfig.senderName}<br>
        VantaPrints</p>
      `;
      
      console.log("Sending test email with enhanced content...");
      const success = await sendTestEmail(subject, emailContent, user);
      
      if (success) {
        toast.success(`Email simulation successful. Check console for details.`);
      } else {
        toast.error("Failed to simulate test email");
      }
      
    } catch (error) {
      console.error('Error in test email simulation:', error);
      toast.error('Email simulation failed. Please check the console for details.');
    }
  };

  const handleFileUpload = useCallback(async (file: File, type: 'productImage' | 'animatedGif') => {
    if (!file) {
      toast.error('No file selected');
      return;
    }

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `public/${type}-${Date.now()}.${fileExt}`;
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size exceeds 5MB limit');
        return;
      }

      // Validate file type
      if (type === 'productImage' && !file.type.startsWith('image/')) {
        toast.error('File must be an image');
        return;
      }
      
      if (type === 'animatedGif' && file.type !== 'image/gif') {
        toast.error('File must be a GIF');
        return;
      }

      // Upload using the anonymous key for public access
      const { data, error } = await supabase.storage
        .from('campaign-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          toast.error('Storage bucket not found. Please ensure "campaign-images" bucket exists in Supabase.');
        } else {
          toast.error(`Upload failed: ${error.message}`);
        }
        throw error;
      }

      if (!data?.path) {
        throw new Error('Upload successful but no path returned');
      }

      const { data: { publicUrl } } = supabase.storage
        .from('campaign-images')
        .getPublicUrl(data.path);

      setUploadedUrls(prev => ({
        ...prev,
        [type]: publicUrl
      }));

      toast.success(`${type === 'productImage' ? 'Product image' : 'Animated GIF'} uploaded successfully`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('File upload failed. Please check if the storage bucket exists and permissions are set correctly.');
    }
  }, [setUploadedUrls]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: 'productImage' | 'animatedGif') => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData(prev => ({
      ...prev,
      [type]: file
    }));

    await handleFileUpload(file, type);
  };

  const toggleEmailSelection = (email: string) => {
    const updatedEmails = emailsWithSelection.map(item => 
      item.email === email 
        ? { ...item, selected: !item.selected }
        : item
    );
    
    setEmailsWithSelection(updatedEmails);
  };

  const handleTemplateSelect = (template: string) => {
    try {
      // Extract subject line from the template
      const templateLines = template.split('\n');
      const subject = templateLines.length > 1 && templateLines[1].startsWith('Subject:') 
        ? templateLines[1].substring(8).trim()
        : generateAISubject(formData.businessCategory, formData.productDescription);
      
      // Update timer config with the selected template
      if (emailsData?.enhancedDescription) {
        // Extract sender from the selected template
        const sender = templateLines[0];
        
        // Create a new template with enhanced description
        const newTemplate = `${sender}\nSubject: ${subject}\n\nHi there,\n\n${emailsData.enhancedDescription}\n\nRegards,\n${emailConfig.senderName}\nVantaPrints`;
        setTimerConfig(prev => ({
          ...prev,
          emailTemplate: newTemplate
        }));
      } else {
        // If no enhanced description yet, just update with the template but ensure it has a polite greeting
        const templateWithPoliteGreeting = templateLines
          .map((line, index) => {
            // Replace "Dear {{recipient}}," with "Hi there,"
            if (line.startsWith('Dear {{recipient}}')) {
              return 'Hi there,';
            }
            // If this is the last line and it's the sender name, replace it with proper signature
            if (index === templateLines.length - 1 && line.trim() === emailConfig.senderName) {
              return `${emailConfig.senderName}\nVantaPrints`;
            }
            return line;
          })
          .join('\n');
        
        setTimerConfig(prev => ({
          ...prev,
          emailTemplate: templateWithPoliteGreeting
        }));
      }
    } catch (error) {
      console.error("Error selecting template:", error);
      toast.error("Failed to update template");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Reset any previous errors and states
      setError(null);
      setTestEmailSent(false);
      
      // Validate and save API key
      if (searchSettings.apiKey) {
        localStorage.setItem('openai_api_key', searchSettings.apiKey);
      }
    
      // Validate form inputs
      if (!formData.businessCategory.trim()) {
        toast.error('Business category is required');
        return;
      }
    
      if (!formData.productDescription.trim()) {
        toast.error('Product description is required');
        return;
      }
    
      // Validate API key
      if (!searchSettings.apiKey) {
        toast.error('OpenAI API key is required. Please add it in the search settings.');
        setShowSearchSettings(true);
        return;
      }
      
      if (!searchSettings.apiKey.startsWith('sk-')) {
        toast.error('Invalid OpenAI API key format. Please check your API key.');
        setShowSearchSettings(true);
        return;
      }
    
      // Make sure we're targeting Malta
      if (!searchSettings.targetCountries.includes('MT')) {
        searchSettings.targetCountries = ['MT'];
        toast.info('Target country set to Malta');
      }
    
      setLoading(true);
      setSearchInProgress(true);
      setEmailsWithSelection([]);
    
      // Start the search for business email addresses
      toast.success('Starting AI-powered search for Maltese business contacts...');
      
      // Search for business emails
      const searchingToast = toast.loading("Searching for Malta business contacts using AI...");
      
      let searchResult;
      
      try {
        // Call our AI API to search for business emails
        searchResult = await searchBusinessEmails(
          formData.businessCategory, 
          searchSettings,
          importedEmails
        );
        
        // Remove the loading toast
        toast.dismiss(searchingToast);
        
        // Make sure we have data before proceeding
        if (!searchResult?.emails?.length) {
          throw new Error("No contacts found. Please try a different search or import contacts manually.");
        }
        
        // Show appropriate message based on number of contacts found
        const contactCount = searchResult.emails.length;
        if (contactCount < 10) {
          toast.success(`Found ${contactCount} initial contacts. You can add more through manual import.`);
        } else {
          toast.success(`Found ${contactCount} relevant Malta business contacts!`);
        }
      } catch (searchError: any) {
        toast.dismiss(searchingToast);
        console.error('Search error:', searchError);
        toast.error(`Error finding business contacts: ${searchError.message || 'Unknown error'}`);
        throw searchError;
      }
      
      if (!searchResult || searchResult.emails.length === 0) {
        throw new Error("Search completed but no valid business contacts were found");
      }
      
      // Calculate country distribution
      const countryDistribution: Record<string, number> = {};
      searchResult.emails.forEach(email => {
        const country = assignCountryFromEmail(email, searchSettings.targetCountries);
        countryDistribution[country] = (countryDistribution[country] || 0) + 1;
      });
      
      // Use AI to enhance the product description for the Malta market
      const enhancingToast = toast.loading("Enhancing product description with AI for Malta market...");
      let enhancedDescription: string;
      
      try {
        // Call our AI API to enhance the product description
        enhancedDescription = await enhanceProductDescription(
          formData.productDescription, 
          formData.businessCategory,
          searchSettings.apiKey
        );
        
        toast.dismiss(enhancingToast);
        toast.success('✓ Created AI-enhanced sales pitch for Malta market');
      } catch (enhanceError: any) {
        toast.dismiss(enhancingToast);
        console.error('Error enhancing description with AI:', enhanceError);
        toast.error(`Could not enhance description with AI: ${enhanceError.message || 'Unknown error'}`);
        
        // Fallback enhancement specific to Malta
        enhancedDescription = `We are excited to introduce our innovative solution designed specifically for ${formData.businessCategory} businesses in Malta. ${formData.productDescription} Our system helps streamline operations while boosting customer engagement, ultimately saving time and reducing costs for your Malta-based business.`;
      }
      
      // Generate AI subject line
      const aiSubject = generateAISubject(formData.businessCategory, formData.productDescription);
      
      const emailResponse: GatheredEmailsResponse = {
        gatheredEmails: searchResult.emails,
        domainDistribution: searchResult.domainDistribution,
        countryDistribution,
        testEmailStatus: `Email simulation completed successfully. See console for details.`,
        notes: `Malta campaign ready for review. Found ${searchResult.emails.length} Malta business contacts across ${Object.keys(searchResult.domainDistribution).length} domains.`,
        enhancedDescription,
        sourceUrls: searchResult.sourceUrls
      };
      
      setEmailsData(emailResponse);
      toast.success('Malta email campaign setup completed with AI enhancement!');
      
      // Update the email template with enhanced description and AI subject
      const newTemplate = `${emailConfig.senderName} <${emailConfig.senderEmail}>\nSubject: ${aiSubject}\n\nHi there,\n\n${enhancedDescription}\n\nRegards,\n${emailConfig.senderName}\nVantaPrints`;
      
      setTimerConfig(prev => ({
        ...prev,
        emailTemplate: newTemplate
      }));
      
    } catch (error: any) {
      console.error('Campaign setup error:', error);
      const errorMessage = error?.message || 'Failed to set up campaign. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setSearchInProgress(false);
    }
  };

  const handleImportedEmails = (emails: string[]) => {
    setImportedEmails(emails);
    toast.success(`${emails.length} emails imported successfully`);
  };

  const handleStartCampaign = () => {
    // Filter out selected (excluded) emails
    const activeEmails = emailsWithSelection.filter(item => !item.selected);
    
    if (activeEmails.length === 0) {
      toast.error('No recipients selected for the campaign');
      return;
    }
    
    // Configure the timer
    setTimerConfig(prev => ({
      ...prev,
      startTime: new Date(),
      totalSent: 0,
      totalToSend: activeEmails.length,
      nextSendTime: new Date(Date.now() + 1000), // Start first email after 1 second
      isPaused: false
    }));
    
    setShowConfirmation(false);
    setShowTimer(true);
    setCampaignInProgress(true);
    toast.success(`Campaign started with ${activeEmails.length} Malta recipients`);
  };

  const handlePauseResume = () => {
    setTimerConfig(prev => ({
      ...prev,
      isPaused: !prev.isPaused,
      nextSendTime: prev.isPaused && prev.nextSendTime 
        ? new Date(Date.now() + prev.interval * 1000) 
        : prev.nextSendTime
    }));
  };

  // Send next email in campaign
  const sendNextEmail = React.useCallback(() => {
    if (timerConfig.isPaused) return;
    
    const sentCount = timerConfig.totalSent;
    const activeEmails = emailsWithSelection.filter(item => !item.selected);
    
    if (sentCount < activeEmails.length) {
      // Get the next recipient
      const nextEmailData = activeEmails[sentCount];
      
      // Create personalized greeting based on the recipient's email
      const greeting = getPoliteGreeting(nextEmailData.email);
      
      // Get the email template and replace placeholder with personalized greeting
      let emailTemplate = timerConfig.emailTemplate;
      
      // Extract subject line from the template
      const templateLines = emailTemplate.split('\n');
      const subject = templateLines.length > 1 && templateLines[1].startsWith('Subject:') 
        ? templateLines[1].substring(8).trim()
        : 'New Campaign Update';
      
      // Replace standard greeting with personalized one
      emailTemplate = emailTemplate.replace(/Hi there,|Hello,|Dear {{recipient}},/, greeting);
      
      // Then replace any remaining {{recipient}} instances with email address
      const emailContent = emailTemplate.replace(/\{\{recipient\}\}/g, nextEmailData.email);
      
      console.log(`Sending email to: ${nextEmailData.email}`);
      console.log('Email content:', emailContent);
      
      // Update the timer configuration
      setTimerConfig(prev => ({
        ...prev,
        totalSent: prev.totalSent + 1,
        nextSendTime: new Date(Date.now() + prev.interval * 1000)
      }));
      
      // Record the sent email in the database
      recordSentEmail(nextEmailData.email, subject, `Malta Campaign from ${new Date().toLocaleDateString()}`);
      
      toast.success(`Email sent to ${nextEmailData.email}`);
    } else {
      // Campaign completed
      setCampaignInProgress(false);
      toast.success('Malta campaign completed successfully!');
      
      setTimerConfig(prev => ({
        ...prev,
        nextSendTime: null
      }));
    }
  }, [timerConfig, emailsWithSelection, setTimerConfig]);

  // Effect to handle sending emails at intervals
  React.useEffect(() => {
    if (!campaignInProgress || timerConfig.isPaused) return;
    
    let timeout: NodeJS.Timeout | undefined;
    
    if (timerConfig.nextSendTime) {
      const now = new Date();
      const timeUntilNext = Math.max(0, timerConfig.nextSendTime.getTime() - now.getTime());
      
      timeout = setTimeout(() => {
        sendNextEmail();
      }, timeUntilNext);
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [campaignInProgress, timerConfig.nextSendTime, timerConfig.isPaused, sendNextEmail]);

  // Use a sample recipient email for preview
  const sampleRecipient = emailsWithSelection.length > 0 
    ? emailsWithSelection[0].email 
    : "info@business.com.mt";

  // If the campaign is running, show the timer view
  if (showTimer) {
    return (
      <>
        <EmailCampaignResults 
          timerConfig={timerConfig}
          setTimerConfig={setTimerConfig}
          emailsWithSelection={emailsWithSelection.filter(item => !item.selected)}
          onPauseResume={handlePauseResume}
          campaignInProgress={campaignInProgress}
          setCampaignInProgress={setCampaignInProgress}
        />
      </>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold flex items-center">
            Malta Campaign Setup
            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center">
              <Sparkles className="h-3 w-3 mr-1" /> AI Enhanced
            </span>
          </h2>
          <button
            type="button"
            onClick={() => setShowSearchSettings(true)}
            className="flex items-center text-sm text-blue-500 hover:text-blue-700"
          >
            <Settings className="h-4 w-4 mr-1" />
            Configure Email Search
          </button>
        </div>

        <div className="bg-blue-50 p-3 rounded-md mb-4">
          <p className="text-sm text-blue-700 flex items-center">
            <Globe className="h-4 w-4 mr-2" />
            <strong>Malta Focus:</strong> This campaign is configured to target businesses in Malta specifically.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="business-category">
              Business Category in Malta
            </label>
            <input
              id="business-category"
              type="text"
              value={formData.businessCategory}
              onChange={(e) => setFormData(prev => ({ ...prev, businessCategory: e.target.value }))}
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              required
              aria-required="true"
              placeholder="e.g., Restaurants, Cafes, Hotels in Malta"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email-count">
              Malta Email Search Target
            </label>
            <div className="flex items-center">
              <div className="w-full px-4 py-2 border rounded-md bg-gray-50 text-gray-700 flex justify-between items-center">
                <span>{searchSettings.minEmails} - {searchSettings.maxEmails} emails</span>
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                  Target: Malta
                </span>
              </div>
            </div>
            {!searchSettings.apiKey && (
              <p className="text-xs text-red-500 mt-1">
                Please set your OpenAI API key in the search settings
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="product-description">
              Product Description for Malta Market
            </label>
            <textarea
              id="product-description"
              value={formData.productDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, productDescription: e.target.value }))}
              rows={4}
              className="w-full px-4 py-2 border rounded-md resize-none focus:ring-2 focus:ring-blue-500"
              required
              aria-required="true"
              placeholder="Describe your product or service for the Malta market. Be specific about features and benefits."
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">
              Will be enhanced with AI for optimal engagement in the Maltese market.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Product Image (optional)
              </label>
              <FileUpload
                id="product-image-upload"
                type="productImage"
                accept="image/*"
                url={uploadedUrls.productImage}
                onFileChange={handleFileChange}
                onRemove={() => setUploadedUrls(prev => ({ ...prev, productImage: '' }))}
                label="Upload Product Image"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Animated GIF (optional)
              </label>
              <FileUpload
                id="animated-gif-upload"
                type="animatedGif"
                accept="image/gif"
                url={uploadedUrls.animatedGif}
                onFileChange={handleFileChange}
                onRemove={() => setUploadedUrls(prev => ({ ...prev, animatedGif: '' }))}
                label="Upload Animated GIF"
              />
            </div>
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading || !formData.businessCategory || !formData.productDescription || !searchSettings.apiKey}
            className="w-full bg-blue-500 text-white py-3 rounded-md hover:bg-blue-600 flex items-center justify-center transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {searchInProgress ? (
              <>
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                AI searching Malta business contacts...
              </>
            ) : (
              <>
                <Search className="h-5 w-5 mr-2" />
                Find Malta Business Email Contacts with AI
              </>
            )}
          </button>
        </div>
      </form>

      {!emailsData && (
        <div className="mt-8">
          <SpreadsheetImport onEmailsImported={handleImportedEmails} />
        </div>
      )}

      {emailsData && (
        <>
          <EmailSelection
            emails={emailsWithSelection}
            onToggleSelection={toggleEmailSelection}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />

          {formData.businessCategory && formData.productDescription && (
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <EmailTemplateGenerator
                businessCategory={formData.businessCategory}
                productDescription={formData.productDescription}
                onSelectTemplate={handleTemplateSelect}
                senderName={emailConfig.senderName}
                senderEmail={emailConfig.senderEmail}
              />
              
              <EmailPreview
                emailTemplate={timerConfig.emailTemplate}
                recipientEmail={sampleRecipient}
                enhancedDescription={emailsData.enhancedDescription}
              />
            </div>
          )}

          <CampaignSummary
            emailsData={emailsData}
            onDispatch={() => setShowConfirmation(true)}
          />
        </>
      )}

      <ConfirmationModal
        show={showConfirmation}
        emailCount={emailsWithSelection.filter(item => !item.selected).length}
        loading={loading}
        onCancel={() => setShowConfirmation(false)}
        onConfirm={handleStartCampaign}
      />

      <EmailSearchSettingsModal
        show={showSearchSettings}
        onClose={() => setShowSearchSettings(false)}
        onSave={setSearchSettings}
        currentSettings={searchSettings}
      />
    </>
  );
}